import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "src/components/layout/dashboard/Layout";
import Breadcrumb from "src/components/ui/Breadcrumb";
import TableV2 from "src/components/ui/TableV2";
import Button from "src/components/ui/Button";
import Card from "src/components/common/card/Card";
import InvoiceDocument from "src/components/invoice/InvoiceDocument";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import { toast } from "react-toastify";
import salesApis from "src/api/salesOrder";
import { currencyFormatter } from "src/utils/common";
import { formatDateTime } from "src/utils/date-time";
import { formatAddress } from "src/utils/formatAddress";
import { generatePDF } from "src/utils/pdfGenerator";
import { printElement } from "src/utils/printElement";
import { Loader2, Download, Printer, Pencil } from "lucide-react";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as salesActions from "src/redux/slices/salesSlice";
import { Badge } from "src/components/ui/Badge";
import useWarehouseLabel from "src/hooks/useWarehouseLabel";

const statusColor = (status: string) =>
  status === "completed" ? "green" : status === "cancelled" ? "red" : "yellow";

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

const SalesOrderDetailsPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<any>(null);
  const [downloading, setDownloading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const invoiceRef = useRef<HTMLDivElement>(null);

  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const tenant = user?.tenantDetails;
  const { singular: warehouseLabel } = useWarehouseLabel();
  const { completeSaleOrder, cancelSaleOrder } = useAppSelector(
    (state) => state.sales
  );

  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" || user?.roleDetails?.name?.toLowerCase() === "admin";
  const canEditSales = isTenantAdmin || (permissions?.sales || []).includes("edit");
  const isOrderOwner = (() => {
    const createdById =
      typeof order?.createdBy === "string" ? order.createdBy : order?.createdBy?._id;
    if (!createdById || !user?._id) return false;
    return String(createdById) === String(user._id);
  })();
  const canEditThisOrder =
    canEditSales && order?.orderStatus === "pending" && (isTenantAdmin || isOrderOwner);

  useEffect(() => {
    if (orderId) getOrderDetails(orderId);
  }, [orderId]);

  useEffect(() => {
    if (completeSaleOrder.data) {
      toast.success("Sale order marked completed");
      dispatch(salesActions.resetCompleteSalesOrder());
      if (orderId) getOrderDetails(orderId);
    } else if (completeSaleOrder.error) {
      toast.error(`Error: ${completeSaleOrder.error}`);
      dispatch(salesActions.resetCompleteSalesOrder());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completeSaleOrder]);

  useEffect(() => {
    if (cancelSaleOrder.data) {
      toast.success("Sale order cancelled");
      dispatch(salesActions.resetCancelSalesOrder());
      setShowCancelModal(false);
      if (orderId) getOrderDetails(orderId);
    } else if (cancelSaleOrder.error) {
      toast.error(`Error: ${cancelSaleOrder.error}`);
      dispatch(salesActions.resetCancelSalesOrder());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelSaleOrder]);

  const handleMarkCompleted = () => {
    if (orderId) dispatch(salesActions.completeSalesOrderRequest(orderId));
  };

  const handleCancelOrder = () => {
    if (orderId) dispatch(salesActions.cancelSalesOrderRequest(orderId));
  };

  const getOrderDetails = async (orderId: string) => {
    try {
      setLoading(true);
      const res = await salesApis.getOrderById(orderId);
      setOrder(res?.data);
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast.error("Failed to fetch order details");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      await generatePDF(invoiceRef, `invoice-${order.orderId}.pdf`);
    } catch (error) {
      toast.error("Failed to generate invoice PDF");
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => printElement();

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="animate-spin text-primary-600" size={28} />
          <span className="ml-3 text-gray-700 dark:text-gray-300">Loading order...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (!order) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center text-gray-500 dark:text-gray-400">
          Order not found or deleted.
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      meta={{
        title: `Order ${order.orderId} | Invyntra Dashboard`,
        description: "View full sale order details and generate invoices.",
        keywords: "sales order details, invoice, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Breadcrumb
            data={[
              { label: "Sales", path: "/app/sale/list" },
              { label: `#${order.orderId}` },
            ]}
          />
          <div className="flex items-center gap-2">
            {order.orderStatus === "completed" && (
              <>
                <Button onClick={handleDownload} isLoading={downloading} disabled={downloading}>
                  <Download size={16} className="mr-1.5" />
                  Download PDF
                </Button>
                <Button variant="outline" onClick={handlePrint}>
                  <Printer size={16} className="mr-1.5" />
                  Print
                </Button>
              </>
            )}
            {canEditThisOrder && (
              <Button onClick={() => navigate(`/app/sale/${order._id}/edit`)}>
                <Pencil size={16} className="mr-1.5" />
                Edit order
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate("/app/sale/list")}>
              Back to sales
            </Button>
          </div>
        </div>

        <div className="space-y-6 pb-4">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
              Order #{order.orderId}
            </h1>
            {order.orderStatus && (
              <Badge color={statusColor(order.orderStatus)} text={capitalize(order.orderStatus)} />
            )}
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Order date: {formatDateTime(order.orderDate || order.createdAt, "DD MMM YYYY")}
            </span>
          </div>

          {/* Customer Info */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-800 mb-3 dark:text-gray-100">
              Customer Information
            </h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700 dark:text-gray-300">
              <p>
                <span className="font-medium">Name: </span>
                {order.customerId?.name}
              </p>
              <p>
                <span className="font-medium">Mobile: </span>
                {order.customerId?.mobile}
              </p>
              <p>
                <span className="font-medium">Email: </span>
                {order.customerId?.email || "-"}
              </p>
              <p>
                <span className="font-medium">Address: </span>
                {formatAddress(order.customerId) || "-"}
              </p>
              <p>
                <span className="font-medium">GSTIN: </span>
                {order.customerId?.gstNumber || "-"}
              </p>
              <p>
                <span className="font-medium">{warehouseLabel}: </span>
                {order.warehouseId?.name || "-"}
              </p>
            </div>
          </Card>

          {/* Products */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-800 mb-3 dark:text-gray-100">
              Ordered Products
            </h3>

            <TableV2
              columns={[
                {
                  key: "productId.name",
                  header: "Product",
                  accessor: ({ row }) => row.productId?.name,
                },
                {
                  key: "sku",
                  header: "SKU",
                  accessor: ({ row }) => row.sku || row.productId?.sku || "-",
                },
                {
                  key: "hsnCode",
                  header: "HSN",
                  accessor: ({ row }) =>
                    row.hsnCode || row.productId?.hsnCode || "-",
                },
                { key: "quantity", header: "Qty", align: "right" },
                {
                  key: "sellingPrice",
                  header: "Price",
                  align: "right",
                  accessor: ({ row }) =>
                    currencyFormatter(row.sellingPrice.toFixed(2)),
                },
                {
                  key: "discountPercent",
                  header: "Disc (%)",
                  align: "right",
                },
                {
                  key: "taxPercent",
                  header: "GST %",
                  align: "right",
                  accessor: ({ row }) => `${row.taxPercent ?? 0}%`,
                },
                {
                  key: "taxAmount",
                  header: "Tax",
                  align: "right",
                  accessor: ({ row }) =>
                    currencyFormatter((row.taxAmount ?? 0).toFixed(2), "IN", 2),
                },
                {
                  key: "totalPrice",
                  header: "Total",
                  align: "right",
                  accessor: ({ row }) =>
                    currencyFormatter(row.totalPrice.toFixed(2), "IN", 2),
                },
              ]}
              data={order.items || []}
            />
          </Card>

          {/* Payment Summary */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-800 mb-3 dark:text-gray-100">
              Payment Summary
            </h3>

            <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700 dark:text-gray-300">
              <p>
                <span className="font-medium text-slate-500 dark:text-gray-400">
                  Subtotal:
                </span>{" "}
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {currencyFormatter(order.subTotal)}
                </span>
              </p>
              <p>
                <span className="font-medium text-slate-500 dark:text-gray-400">
                  Tax:
                </span>{" "}
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {currencyFormatter(order.taxAmount)}
                </span>
              </p>
              <p>
                <span className="font-medium text-slate-500 dark:text-gray-400">
                  Discount:
                </span>{" "}
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {currencyFormatter(order.discount)}
                </span>
              </p>
              <p>
                <span className="font-medium text-slate-500 dark:text-gray-400">
                  Grand Total:
                </span>{" "}
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {currencyFormatter(order.grandTotal)}
                </span>
              </p>
              <p>
                <span className="font-medium text-slate-500 dark:text-gray-400">
                  Payment Mode:
                </span>{" "}
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {order.paymentMode?.toUpperCase()}
                </span>
              </p>
              <p className="flex items-center gap-2">
                <span className="font-medium text-slate-500 dark:text-gray-400">
                  Payment Status:
                </span>
                <Badge
                  text={
                    order.paymentStatus
                      ? order.paymentStatus.charAt(0).toUpperCase() +
                        order.paymentStatus.slice(1)
                      : "-"
                  }
                  color={order.paymentStatus === "paid" ? "green" : "yellow"}
                />
              </p>
            </div>
          </Card>

          {/* Meta Info */}
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <p>
              <span className="font-medium text-gray-700 dark:text-gray-300">
                Created By:
              </span>{" "}
              {order.createdBy?.firstName || "Staff"}
            </p>
            <p>
              <span className="font-medium text-gray-700 dark:text-gray-300">
                Created At:
              </span>{" "}
              {formatDateTime(order.createdAt, "DD/MM/YYYY hh:mm A")}
            </p>
          </div>

          {/* Fulfillment actions */}
          {order.orderStatus === "pending" ? (
            <Card>
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                Fulfill this order
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Stock is currently reserved at{" "}
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {order.warehouseId?.name || `the selected ${warehouseLabel.toLowerCase()}`}
                </span>
                . Mark it completed once the order is handed over, or cancel to
                release the hold.
              </p>
              <div className="mt-3 flex items-center gap-3">
                <Button
                  onClick={handleMarkCompleted}
                  isLoading={completeSaleOrder.loading}
                  disabled={completeSaleOrder.loading}
                >
                  Mark completed
                </Button>
                <Button
                  variant="outline"
                  color="danger"
                  onClick={() => setShowCancelModal(true)}
                  disabled={completeSaleOrder.loading}
                >
                  Cancel order
                </Button>
              </div>
            </Card>
          ) : order.orderStatus === "completed" ? (
            <Card>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Completed on{" "}
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {formatDateTime(order.completedAt, "DD MMM YYYY, hh:mm A")}
                </span>
                . Stock has been deducted from{" "}
                {order.warehouseId?.name || `the ${warehouseLabel.toLowerCase()}`}.
              </p>
            </Card>
          ) : order.orderStatus === "cancelled" ? (
            <Card>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This order was cancelled — its stock reservation has been released.
              </p>
            </Card>
          ) : null}
        </div>
      </div>

      {showCancelModal && (
        <ConfirmationModal
          title="Cancel sale order"
          message="Are you sure you want to cancel this order? Its reserved stock will be released back for other sales."
          variant="danger"
          confirmLabel="Cancel order"
          onConfirm={handleCancelOrder}
          onCancel={() => setShowCancelModal(false)}
          open={showCancelModal}
          isLoading={cancelSaleOrder.loading}
        />
      )}

      <div className="fixed left-[-9999px] top-0" aria-hidden>
        <InvoiceDocument
          ref={invoiceRef}
          order={order}
          tenant={tenant}
          className="print-target"
        />
      </div>
    </DashboardLayout>
  );
};

export default SalesOrderDetailsPage;
